import { GridColDef } from "@mui/x-data-grid-pro";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DataQualityCell } from "../components/DataQualityCell";
import { ConsumptionRow } from "../models/consumptionRow";
import { energyDataStatusOrder } from "../models/energyDataQuality";
import { decimalFormatter } from "../utilities/decimalFormatter";
import { formatDateTime } from "@/utilities/formatDateTime";

/**
 * Columns not worth the width by default. Every one of them is a detail behind a figure that is
 * already summarised elsewhere on the row, so they are there for a reader digging into a
 * particular vehicle rather than for the overview.
 */
export const CONSUMPTION_HIDDEN_COLUMNS = {
  measuredDistanceKm: false,
  measuredFrom: false,
  measuredTo: false,
  fuelSource: false,
};

/**
 * The columns of the consumption view.
 *
 * Units belong in the header and never in the cell: a cell holding "8,2 l/100 km" is text, and a
 * column of text cannot be summed, sorted numerically or charted after the export. Declaring the
 * column as a number is also what makes the Excel export write a real number into it.
 *
 * @param vinDecodedByDeviceId - Make and model per vehicle, from the decoded VIN.
 * @returns The column definitions.
 */
export function useConsumptionColumns(
  vinDecodedByDeviceId: Map<string, string>,
): GridColDef<ConsumptionRow>[] {
  const { t, i18n } = useTranslation();
  const language = i18n.language;

  // Memoised because DataGridPro rebuilds its whole column state on a new array identity, and
  // this page re-renders once per arriving vehicle — hundreds of times while a fleet is read.
  return useMemo<GridColDef<ConsumptionRow>[]>(() => {
    const decimals = decimalFormatter(language);

    return [
    {
      field: "status",
      headerName: t("energyReport.columns.quality", "Datenqualität"),
      minWidth: 180,
      // Sorted by severity rather than by the label, so the vehicles worth looking at come first.
      sortComparator: (first, second) =>
        energyDataStatusOrder(first as ConsumptionRow["status"]) -
        energyDataStatusOrder(second as ConsumptionRow["status"]),
      renderCell: ({ row }) => <DataQualityCell row={row} />,
    },
    {
      field: "name",
      headerName: t("energyReport.columns.asset", "Fahrzeug"),
      minWidth: 200,
      flex: 1.2,
    },
    {
      field: "licensePlate",
      headerName: t("energyReport.columns.licensePlate", "Kennzeichen"),
      minWidth: 130,
    },
    {
      field: "vinDecoded",
      headerName: t("energyReport.columns.vinDecoded", "Fahrzeugtyp (VIN)"),
      minWidth: 180,
      flex: 1,
      sortable: false,
      valueGetter: (_value, row) => vinDecodedByDeviceId.get(row.id) ?? "",
    },
    {
      field: "fuelRatePer100Km",
      headerName: t("energyReport.columns.fuelRate", "Kraftstoff (l/100 km)"),
      description: t(
        "energyReport.columns.rateHint",
        "Bezogen auf die Strecke innerhalb des Zeitraums, für den Zählerstände vorliegen.",
      ),
      type: "number",
      minWidth: 160,
      valueFormatter: value => decimals(value as number | null),
    },
    {
      field: "electricRatePer100Km",
      headerName: t("energyReport.columns.electricRate", "Strom (kWh/100 km)"),
      description: t(
        "energyReport.columns.rateHint",
        "Bezogen auf die Strecke innerhalb des Zeitraums, für den Zählerstände vorliegen.",
      ),
      type: "number",
      minWidth: 160,
      valueFormatter: value => decimals(value as number | null),
    },
    {
      field: "fuelUsedLiters",
      headerName: t("energyReport.columns.fuelUsed", "Kraftstoff verbraucht (l)"),
      type: "number",
      minWidth: 180,
      valueFormatter: value => decimals(value as number | null),
    },
    {
      field: "electricUsedKWh",
      headerName: t("energyReport.columns.electricUsed", "Strom verbraucht (kWh)"),
      type: "number",
      minWidth: 180,
      valueFormatter: value => decimals(value as number | null),
    },
    {
      field: "filledLiters",
      headerName: t("energyReport.columns.filled", "Getankt (l)"),
      type: "number",
      minWidth: 130,
      valueFormatter: value => decimals(value as number | null),
    },
    {
      field: "fuelDeviationLiters",
      headerName: t("energyReport.columns.fuelDeviation", "Abweichung getankt/verbraucht (l)"),
      description: t(
        "energyReport.columns.fuelDeviationHint",
        "Getankte Menge minus gemessenem Verbrauch. Ein deutlicher Überschuss deutet auf einen fehlerhaften Messwert oder Kraftstoffverlust hin.",
      ),
      type: "number",
      minWidth: 200,
      valueFormatter: value => decimals(value as number | null),
    },
    {
      field: "chargedKWh",
      headerName: t("energyReport.columns.charged", "Geladen (kWh)"),
      type: "number",
      minWidth: 150,
      valueFormatter: value => decimals(value as number | null),
    },
    {
      field: "chargeDurationHours",
      headerName: t("energyReport.columns.chargeDuration", "Ladezeit (h)"),
      type: "number",
      minWidth: 130,
      valueFormatter: value => decimals(value as number | null),
    },
    {
      field: "distanceKm",
      headerName: t("energyReport.columns.distance", "Strecke (km)"),
      type: "number",
      minWidth: 130,
      valueFormatter: value => decimals(value as number | null, 0),
    },
    {
      field: "measuredDistanceKm",
      headerName: t("energyReport.columns.measuredDistance", "davon gemessen (km)"),
      description: t(
        "energyReport.columns.measuredDistanceHint",
        "Strecke innerhalb des Zeitraums, für den Zählerstände vorliegen. Nur diese steckt in den Verbrauchswerten.",
      ),
      type: "number",
      minWidth: 170,
      valueFormatter: value => decimals(value as number | null, 0),
    },
    {
      field: "measuredFrom",
      headerName: t("energyReport.columns.measuredFrom", "Messwerte ab"),
      minWidth: 160,
      valueFormatter: value => formatDateTime(value as Date | undefined, language),
    },
    {
      field: "measuredTo",
      headerName: t("energyReport.columns.measuredTo", "Messwerte bis"),
      minWidth: 160,
      valueFormatter: value => formatDateTime(value as Date | undefined, language),
    },
    {
      field: "fuelSource",
      headerName: t("energyReport.columns.fuelSource", "Quelle Kraftstoffzähler"),
      minWidth: 190,
      valueGetter: (_value, row) => {
        if (row.fuelSource === "vehicle") return t("energyReport.fuelSource.vehicle", "Fahrzeug");
        if (row.fuelSource === "telematics") {
          return t("energyReport.fuelSource.telematics", "Telematikbox");
        }
        return "";
      },
    },
  ];
  }, [t, language, vinDecodedByDeviceId]);
}
